import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Smile, Send, Users, BarChart3, MailCheck, TrendingUp, AlertTriangle, Download, Filter, RefreshCw, Plus, ChevronRight, Building2, Trash2, GripVertical, Save, RotateCcw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";
import { AnnualReportButton } from "@/components/satisfaction/AnnualReportButton";

const STATUS_TONE = {
  "Not Sent": "bg-slate-100 text-slate-700 border-slate-200",
  "Sent": "bg-blue-50 text-blue-700 border-blue-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Follow-Up Required": "bg-rose-50 text-rose-700 border-rose-200",
};
const fmt = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

async function apiGet(path) { const r = await api.get(path); return r.data; }
async function apiPost(path, body) { const r = await api.post(path, body); return r.data; }
async function apiPatch(path, body) { const r = await api.patch(path, body); return r.data; }

export default function CustomerSatisfactionPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const clientQP = params.get("client");
  const [clientSlug, setClientSlug] = useState(clientQP || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [openSend, setOpenSend] = useState(false);
  const [openCustomer, setOpenCustomer] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => {
    endpoints.listClients().then(setClients).catch(() => {});
    apiGet(`/client-customers?client_slug=${clientSlug}`).then(setCustomers).catch(() => setCustomers([]));
    apiGet(`/customer-surveys?client_slug=${clientSlug}`).then(setSurveys).catch(() => setSurveys([]));
    apiGet(`/customer-satisfaction/dashboard?client_slug=${clientSlug}`).then(setDashboard).catch(() => setDashboard(null));
    apiGet(`/customer-satisfaction/settings?client_slug=${clientSlug}`).then(setSettings).catch(() => setSettings(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const isClient = scope?.role === "client" || scope?.role === "client_admin" || scope?.role === "client_employee";

  const sendReminder = async (sid) => {
    try { await apiPost(`/customer-surveys/${sid}/remind`); toast.success("Reminder sent"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reminder failed"); }
  };
  const createNcr = async (sid) => {
    try { const r = await apiPost(`/customer-surveys/${sid}/create-ncr`); toast.success(`NCR ${r.ncr_number} created`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create NCR"); }
  };
  const updateThreshold = async (v) => {
    try { const s = await apiPatch(`/customer-satisfaction/settings`, { client_slug: clientSlug, satisfaction_threshold: v }); setSettings(s); toast.success("Threshold updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveQuestions = async (questions) => {
    try {
      const s = await apiPatch(`/customer-satisfaction/settings`, { client_slug: clientSlug, survey_questions: questions });
      setSettings(s);
      toast.success("Survey questions saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center shadow-md">
            <Smile className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Customer Satisfaction</h1>
            <p className="text-xs text-slate-500">Send surveys to your customers, track responses and manage follow-ups.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="cs-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="cs-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <a href={`${API}/customer-surveys-export.csv?client_slug=${clientSlug}`}
             className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="cs-export">
            <Download className="h-4 w-4" /> Export CSV
          </a>
          <AnnualReportButton clientSlug={clientSlug} />
          <Button data-testid="cs-send-button" className="brand-gradient text-white shadow" onClick={() => setOpenSend(true)}>
            <Send className="h-4 w-4 mr-1" /> Send Customer Satisfaction Survey
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="cs-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="register" data-testid="cs-tab-register">Register</TabsTrigger>
          <TabsTrigger value="customers" data-testid="cs-tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="settings" data-testid="cs-tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard" className="pt-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Overall satisfaction" value={dashboard?.overall_percent != null ? `${dashboard.overall_percent}%` : "—"} tone="emerald" />
            <Stat label="Surveys sent" value={dashboard?.total_sent ?? 0} tone="indigo" />
            <Stat label="Responses" value={dashboard?.total_completed ?? 0} tone="sky" />
            <Stat label="Response rate" value={dashboard ? `${dashboard.response_rate}%` : "—"} tone="purple" />
            <Stat label="Follow-ups" value={dashboard?.follow_up_count ?? 0} tone="rose" />
            <Stat label="Threshold" value={dashboard ? `${dashboard.threshold}%` : "—"} tone="slate" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="glass-card"><CardContent className="p-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-600" /> Average score per question</div>
              <div className="space-y-2">
                {(dashboard?.per_question || []).map(q => (
                  <div key={q.question_id}>
                    <div className="flex justify-between text-xs text-slate-500 mb-0.5"><span>{q.label}</span><span>{q.average_score}/5 · {q.average_percent}%</span></div>
                    <div className="h-2 rounded bg-slate-100 overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${q.average_percent}%` }} /></div>
                  </div>
                ))}
                {(!dashboard?.per_question || dashboard.per_question.length === 0) && <div className="text-xs text-slate-400 text-center py-6">No responses yet — send some surveys to see averages.</div>}
              </div>
            </CardContent></Card>

            <Card className="glass-card"><CardContent className="p-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> Satisfaction trend</div>
              <MiniTrend points={dashboard?.trend || []} />
            </CardContent></Card>
          </div>

          <Card className="glass-card"><CardContent className="p-0">
            <div className="p-4 pb-2 text-sm font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-600" /> Customer comparison</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70 border-b"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3">Customer</th><th className="py-2 px-3">Overall</th><th className="py-2 px-3">Latest</th>
                  <th className="py-2 px-3">Last survey</th><th className="py-2 px-3">Trend</th><th className="py-2 px-3">Sparkline</th>
                </tr></thead>
                <tbody>
                  {(dashboard?.per_customer || []).map(c => (
                    <tr key={c.customer_id} className="border-b hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenCustomer(customers.find(x => x.id === c.customer_id))}>
                      <td className="py-2 px-3 font-medium">{c.business_name}</td>
                      <td className="py-2 px-3">{c.overall_percent != null ? `${c.overall_percent}%` : "—"}</td>
                      <td className="py-2 px-3">{c.latest_percent != null ? `${c.latest_percent}%` : "—"}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{fmt(c.last_survey_at)}</td>
                      <td className="py-2 px-3 text-lg">{c.trend}</td>
                      <td className="py-2 px-3"><Sparkline points={c.sparkline} /></td>
                    </tr>
                  ))}
                  {(!dashboard?.per_customer || dashboard.per_customer.length === 0) && (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-500">No customer data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Register */}
        <TabsContent value="register" className="pt-4">
          <Card className="glass-card"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70 border-b"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3">Customer</th><th className="py-2 px-3">Contact</th><th className="py-2 px-3">Email</th>
                  <th className="py-2 px-3">Sent</th><th className="py-2 px-3">Response</th><th className="py-2 px-3">Score</th>
                  <th className="py-2 px-3">Status</th><th className="py-2 px-3">Follow-up</th><th className="py-2 px-3"></th>
                </tr></thead>
                <tbody>
                  {surveys.map(s => (
                    <tr key={s.id} data-testid={`cs-survey-row-${s.id}`} className="border-b hover:bg-slate-50/60">
                      <td className="py-2 px-3 font-medium">{s.business_name}</td>
                      <td className="py-2 px-3">{s.contact_person}</td>
                      <td className="py-2 px-3 text-xs">{s.email}</td>
                      <td className="py-2 px-3 text-xs">{fmt(s.sent_at)}</td>
                      <td className="py-2 px-3 text-xs">{fmt(s.response_received_at)}</td>
                      <td className="py-2 px-3">{s.overall_percent != null ? `${s.overall_percent}%` : "—"}</td>
                      <td className="py-2 px-3"><Badge className={STATUS_TONE[s.status]}>{s.status}</Badge></td>
                      <td className="py-2 px-3">{s.follow_up_required ? <Badge className="bg-rose-100 text-rose-800">Required</Badge> : <span className="text-slate-400 text-xs">No</span>}</td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        {s.status !== "Completed" && s.status !== "Follow-Up Required" && (
                          <Button size="sm" variant="ghost" onClick={() => sendReminder(s.id)} data-testid={`cs-remind-${s.id}`} title={s.reminder_sent_at ? `Reminder sent ${fmt(s.reminder_sent_at)}` : "Send reminder"}>
                            {s.reminder_sent_at ? <MailCheck className="h-4 w-4 text-emerald-600" /> : <MailCheck className="h-4 w-4" />}
                          </Button>
                        )}
                        {s.follow_up_required && !s.ncr_id && (
                          <Button size="sm" variant="outline" className="text-rose-700 border-rose-300" onClick={() => createNcr(s.id)} data-testid={`cs-createncr-${s.id}`}>
                            <AlertTriangle className="h-4 w-4 mr-1" /> Create NCR
                          </Button>
                        )}
                        {s.ncr_id && <Badge variant="secondary" className="text-[10px]">NCR linked</Badge>}
                      </td>
                    </tr>
                  ))}
                  {surveys.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500">No surveys sent yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Customers */}
        <TabsContent value="customers" className="pt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setAddOpen(true)} data-testid="cs-add-customer"><Plus className="h-4 w-4 mr-1" /> Add customer</Button>
          </div>
          <Card className="glass-card"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70 border-b"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="py-2 px-3">Business</th><th className="py-2 px-3">Contact</th><th className="py-2 px-3">Email</th><th className="py-2 px-3">Phone</th>
                </tr></thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.id} className="border-b hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenCustomer(c)}>
                      <td className="py-2 px-3 font-medium">{c.business_name}</td>
                      <td className="py-2 px-3">{c.contact_person}</td>
                      <td className="py-2 px-3 text-xs">{c.email}</td>
                      <td className="py-2 px-3 text-xs">{c.phone || "—"}</td>
                    </tr>
                  ))}
                  {customers.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-slate-500">No customers yet — add one to start sending surveys.</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* Settings */}
        <TabsContent value="settings" className="pt-4">
          <Card className="glass-card"><CardContent className="p-4 space-y-4">
            <div>
              <div className="text-sm font-semibold">Minimum acceptable score</div>
              <p className="text-xs text-slate-500 mb-2">Responses below this % are flagged Follow-Up Required.</p>
              <div className="flex items-center gap-2 max-w-xs">
                <Input type="number" min={10} max={100} defaultValue={settings?.satisfaction_threshold ?? 70} data-testid="cs-threshold" onBlur={e => updateThreshold(Number(e.target.value))} />
                <span className="text-slate-500">%</span>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold">Survey template (approved)</div>
              <p className="text-xs text-slate-500 mb-3">Add, rename or remove the questions asked to customers. Every question is answered on a 1–5 scale.</p>
              <QuestionsEditor
                key={settings?.survey_template?.questions?.map(q => q.id).join("|") || "empty"}
                initial={settings?.survey_template?.questions || []}
                onSave={saveQuestions}
              />
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <SendSurveyModal open={openSend} onOpenChange={setOpenSend} clientSlug={clientSlug} customers={customers} settings={settings} onSent={load} />
      <CustomerHistoryModal customer={openCustomer} open={!!openCustomer} onOpenChange={o => !o && setOpenCustomer(null)} surveys={surveys.filter(s => s.customer_id === openCustomer?.id)} />
      <AddCustomerModal open={addOpen} onOpenChange={setAddOpen} clientSlug={clientSlug} onAdded={load} />
    </div>
  );
}

function Stat({ label, value, tone = "indigo" }) {
  const t = { indigo: "text-indigo-600", emerald: "text-emerald-600", sky: "text-sky-600", purple: "text-purple-600", rose: "text-rose-600", slate: "text-slate-600" }[tone];
  return <Card className="glass-card"><CardContent className="p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div><div className={`text-xl font-extrabold mt-0.5 ${t}`}>{value}</div></CardContent></Card>;
}

function QuestionsEditor({ initial, onSave }) {
  const [rows, setRows] = useState(initial || []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const initialSnapshot = useMemo(() => JSON.stringify((initial || []).map(q => ({ id: q.id, label: q.label }))), [initial]);
  useEffect(() => { setRows(initial || []); setDirty(false); }, [initialSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLabel = (idx, label) => {
    setRows(r => r.map((q, i) => (i === idx ? { ...q, label } : q)));
    setDirty(true);
  };
  const remove = (idx) => {
    setRows(r => r.filter((_, i) => i !== idx));
    setDirty(true);
  };
  const add = () => {
    setRows(r => [...r, { id: `q_new_${r.length + 1}_${Date.now().toString(36)}`, label: "" }]);
    setDirty(true);
  };
  const move = (idx, dir) => {
    setRows(r => {
      const j = idx + dir;
      if (j < 0 || j >= r.length) return r;
      const copy = [...r];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
    setDirty(true);
  };
  const reset = () => { setRows(initial || []); setDirty(false); };
  const submit = async () => {
    const cleaned = rows.map(q => ({ id: q.id, label: (q.label || "").trim() })).filter(q => q.label);
    if (cleaned.length === 0) { toast.error("At least one question required"); return; }
    setBusy(true);
    try { await onSave(cleaned); setDirty(false); } finally { setBusy(false); }
  };

  return (
    <div data-testid="cs-questions-editor" className="space-y-2">
      {rows.length === 0 && <div className="text-xs text-slate-500 italic border border-dashed rounded-lg px-3 py-4 text-center">No questions yet. Click <b>Add question</b> below.</div>}
      {rows.map((q, i) => (
        <div key={q.id || i} data-testid={`cs-question-row-${i}`} className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-2">
          <div className="flex flex-col text-slate-400">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-indigo-600 disabled:opacity-30 leading-none text-xs" aria-label="Move up">▲</button>
            <button type="button" onClick={() => move(i, +1)} disabled={i === rows.length - 1} className="hover:text-indigo-600 disabled:opacity-30 leading-none text-xs" aria-label="Move down">▼</button>
          </div>
          <span className="text-[10px] font-mono text-slate-400 w-5 text-right shrink-0">{i + 1}.</span>
          <Input
            data-testid={`cs-question-input-${i}`}
            value={q.label}
            placeholder="e.g. Ease of onboarding"
            maxLength={140}
            onChange={e => setLabel(i, e.target.value)}
            className="flex-1"
          />
          <span className="text-[10px] text-slate-400 shrink-0">1–5</span>
          <Button
            variant="ghost"
            size="sm"
            data-testid={`cs-question-remove-${i}`}
            onClick={() => remove(i)}
            aria-label="Remove question"
          >
            <Trash2 className="h-4 w-4 text-rose-500" />
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={add} data-testid="cs-question-add" disabled={rows.length >= 15}>
          <Plus className="h-4 w-4 mr-1" /> Add question
        </Button>
        {rows.length >= 15 && <span className="text-[11px] text-slate-500">Max 15 questions</span>}
        <div className="flex-1" />
        {dirty && (
          <>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="cs-questions-reset">
              <RotateCcw className="h-4 w-4 mr-1" /> Undo
            </Button>
            <Button size="sm" className="brand-gradient text-white" onClick={submit} disabled={busy} data-testid="cs-questions-save">
              <Save className="h-4 w-4 mr-1" /> {busy ? "Saving…" : "Save questions"}
            </Button>
          </>
        )}
      </div>
      <p className="text-[11px] text-slate-400 pt-1">Existing sent surveys keep their original questions. Changes apply to new surveys only.</p>
    </div>
  );
}

function Sparkline({ points = [] }) {
  if (!points || points.length === 0) return <span className="text-slate-300">—</span>;
  const w = 80, h = 24, max = 100, min = 0;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const pts = points.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / (max - min)) * h).toFixed(1)}`).join(" ");
  return <svg width={w} height={h} className="inline-block"><polyline points={pts} fill="none" stroke="#6366f1" strokeWidth="2" /></svg>;
}

function MiniTrend({ points = [] }) {
  if (!points || points.length === 0) return <div className="text-xs text-slate-400 text-center py-6">Trend appears after your first response.</div>;
  const w = 480, h = 120, max = 100, min = 0;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const pts = points.map((p, i) => `${i * step},${h - ((p.average_percent - min) / (max - min)) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h + 20}`} className="w-full">
      <polyline points={pts} fill="none" stroke="#10b981" strokeWidth="2" />
      {points.map((p, i) => (<g key={p.month}><circle cx={i * step} cy={h - ((p.average_percent - min) / (max - min)) * h} r="3" fill="#10b981" /><text x={i * step} y={h + 14} fontSize="9" textAnchor="middle" fill="#64748b">{p.month.slice(5)}</text></g>))}
    </svg>
  );
}

function SendSurveyModal({ open, onOpenChange, clientSlug, customers, settings, onSent }) {
  const [selected, setSelected] = useState([]);
  const [sendAll, setSendAll] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setSelected([]); setSendAll(false); setPreview(false); } }, [open]);
  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const chosen = sendAll ? customers : customers.filter(c => selected.includes(c.id));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await apiPost(`/customer-surveys/send`, { client_slug: clientSlug, customer_ids: selected, send_all: sendAll });
      toast.success(`${r.sent} survey${r.sent === 1 ? "" : "s"} sent`);
      onSent?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="cs-send-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4 text-indigo-600" /> Send Customer Satisfaction Survey</DialogTitle>
          <DialogDescription>Pick one or more customers — the survey is sent from your company's identity with a unique, no-login link.</DialogDescription></DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <label className="flex items-center gap-2 rounded-md border p-2 bg-indigo-50/30">
              <Checkbox checked={sendAll} onCheckedChange={setSendAll} data-testid="cs-send-all" /><span className="text-sm font-semibold">Send to all customers ({customers.length})</span>
            </label>
            <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
              {customers.map(c => (
                <label key={c.id} className={`flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer ${sendAll ? "opacity-60" : ""}`}>
                  <Checkbox disabled={sendAll} checked={sendAll || selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`cs-pick-${c.id}`} />
                  <div className="flex-1"><div className="text-sm font-medium">{c.business_name}</div><div className="text-xs text-slate-500">{c.contact_person} · {c.email}</div></div>
                </label>
              ))}
              {customers.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No customers yet — add customers first.</div>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border bg-slate-50/50 p-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Preview email</div>
              <div className="text-sm font-semibold">{`Your Company — Customer satisfaction survey`}</div>
              <p className="text-sm mt-2 text-slate-600">Hello there, this short survey takes under a minute and covers:</p>
              <ul className="text-sm pl-4 list-disc text-slate-600">
                {(settings?.survey_template?.questions || []).map(q => <li key={q.id}>{q.label}</li>)}
              </ul>
              <div className="mt-2 text-xs text-slate-500">Recipients ({chosen.length}): {chosen.slice(0, 5).map(c => c.email).join(", ")}{chosen.length > 5 ? `, +${chosen.length - 5} more` : ""}</div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          {!preview ? (
            <Button data-testid="cs-preview" onClick={() => setPreview(true)} disabled={chosen.length === 0}>Preview →</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setPreview(false)}>Back</Button>
              <Button data-testid="cs-send-confirm" className="brand-gradient text-white" onClick={submit} disabled={busy || chosen.length === 0}>
                <Send className="h-4 w-4 mr-1" /> {busy ? "Sending..." : `Send to ${chosen.length}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerHistoryModal({ customer, open, onOpenChange, surveys }) {
  if (!customer) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="cs-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-4 w-4 text-sky-600" /> {customer.business_name}</DialogTitle>
          <DialogDescription>{customer.contact_person} · {customer.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {surveys.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No surveys yet.</div>}
          {surveys.map(s => (
            <div key={s.id} className="rounded-lg border p-3 bg-white">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-semibold">{fmt(s.sent_at)}</div>
                <Badge className={STATUS_TONE[s.status]}>{s.status}</Badge>
              </div>
              <div className="text-xs text-slate-500">{s.overall_percent != null ? `Overall: ${s.overall_percent}%` : "No response yet"}</div>
              {(s.responses || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {s.responses.map(r => (
                    <div key={r.question_id} className="text-xs flex justify-between"><span>{r.label}</span><span className="font-mono">{r.score}/5</span></div>
                  ))}
                </div>
              )}
              {s.comments && <div className="mt-2 text-xs italic text-slate-600 border-l-2 border-indigo-300 pl-2">"{s.comments}"</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddCustomerModal({ open, onOpenChange, clientSlug, onAdded }) {
  const [form, setForm] = useState({ business_name: "", contact_person: "", email: "", phone: "" });
  useEffect(() => { if (open) setForm({ business_name: "", contact_person: "", email: "", phone: "" }); }, [open]);
  const submit = async () => {
    if (!form.business_name || !form.email) { toast.error("Business name and email required"); return; }
    try { await apiPost(`/client-customers?client_slug=${clientSlug}`, form); toast.success("Customer added"); onAdded?.(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cs-add-customer-modal">
        <DialogHeader><DialogTitle>Add customer</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><Label className="text-xs">Business name</Label><Input data-testid="cs-cust-name" value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} /></div>
          <div><Label className="text-xs">Contact person</Label><Input data-testid="cs-cust-contact" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
          <div><Label className="text-xs">Email</Label><Input data-testid="cs-cust-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs">Phone (optional)</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button className="brand-gradient text-white" onClick={submit} data-testid="cs-cust-save">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
